/* ==========================================================================
   Alpine: Variant handling
   - Adds: availability matrix for option1 -> option2, so you can surface
           “this color only exists in these sizes” (more obvious UX).
   - Keeps: existing firstOptionHasAvailableChildren / secondOptionHasAvailableChildren
   ========================================================================== */

(function () {
  'use strict';

  function getShopRoot() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
    } catch (_) {}
    return '/';
  }

  function initLiquifyCustom() {
    if (initLiquifyCustom._done) return;
    initLiquifyCustom._done = true;

    /* ------------------------------------------------------------------------
       Safe money formatter
       ------------------------------------------------------------------------ */
    if (typeof window.formatMoney !== 'function') {
      window.formatMoney = function formatMoney(cents) {
        try {
          if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            return window.Shopify.formatMoney(cents);
          }
        } catch (_) {}

        try {
          if (window.LiquifyHelper && typeof window.LiquifyHelper.moneyFormat === 'function') {
            return window.LiquifyHelper.moneyFormat(cents, true);
          }
        } catch (_) {}

        const n = Number(cents);
        if (!Number.isFinite(n)) return '';
        return (n / 100).toFixed(2);
      };
    }

    /* ------------------------------------------------------------------------
       Core factory
       ------------------------------------------------------------------------ */
    const handleVariantFactory = (
      pProduct = null,
      optionsByName = null,
      selectedOrFirstAvailable = null,
      scentColorsArg = null
    ) => {
      const normalizeImageSrc = (img) => {
        if (!img) return '';
        if (typeof img === 'string') return img;
        if (img.src) return img.src;
        if (img.url) return img.url;
        if (img.preview_image && img.preview_image.src) return img.preview_image.src;
        if (img.featured_media && img.featured_media.preview_image && img.featured_media.preview_image.src) {
          return img.featured_media.preview_image.src;
        }
        return '';
      };

      const normalizeVariant = (v) => {
        if (!v) return null;
        const out = { ...v };

        out.featured_image = normalizeImageSrc(v.featured_image) || normalizeImageSrc(v.featured_media) || '';

        if (!Array.isArray(out.options)) {
          const opts = [];
          if (out.option1) opts.push(out.option1);
          if (out.option2) opts.push(out.option2);
          if (out.option3) opts.push(out.option3);
          out.options = opts;
        }

        out.quantity = typeof out.quantity === 'number' ? out.quantity : 1;
        out.addToCartButton = typeof out.addToCartButton === 'boolean' ? out.addToCartButton : true;

        const rawPrice = out.price ?? out._price ?? 0;
        out._price = Number.isFinite(Number(rawPrice)) ? Number(rawPrice) : 0;

        try {
          Object.defineProperty(out, 'price', {
            get() {
              return this._price;
            },
            set(val) {
              const n = Number(val);
              if (!Number.isFinite(n)) throw new Error('Price must be a number');
              this._price = n;
            },
          });
        } catch (_) {}

        return out;
      };

      const normalizeProduct = (p) => {
        if (!p) return null;
        const out = { ...p };

        out.options_by_name = optionsByName || out.options_by_name || {};
        out.variants = Array.isArray(out.variants) ? out.variants.map(normalizeVariant) : [];

        const initial =
          normalizeVariant(selectedOrFirstAvailable) ||
          (out.variants.find((v) => v && v.available) || out.variants[0] || null);

        out.initial_selected_or_first_available_variant = initial;
        out.selected_or_first_available_variant = initial;

        if (out.featured_image && typeof out.featured_image === 'object') {
          out.featured_image = { ...out.featured_image, src: normalizeImageSrc(out.featured_image) };
        }

        return out;
      };

      return {
        /* ----------------------------------------
           Visual helpers
           ---------------------------------------- */
        scentColors: scentColorsArg || window.scentColors || {},
        getColor(name) {
          const key = (name || '').toLowerCase();
          return this.scentColors && this.scentColors[key] ? this.scentColors[key] : 'transparent';
        },
        isSwatchOption(optionName) {
          const n = (optionName || '').toLowerCase();
          return n === 'scent' || n === 'color' || n === 'colour';
        },

        getOptionIndex(optionName) {
          const options = this.product?.options || [];
          return options.findIndex(
            (name) => String(name).toLowerCase() === String(optionName || '').toLowerCase()
          );
        },

        getCurrentSelections() {
          const options = this.product?.options || [];
          const variant = this.selected_or_first_available_variant;
          return options.map((name, index) => {
            return (
              this.filter?.[name] ??
              variant?.options?.[index] ??
              variant?.[`option${index + 1}`] ??
              null
            );
          });
        },

        isOptionValueAvailable(optionName, optionValue) {
          const variants = this.product?.variants || [];
          const options = this.product?.options || [];
          const index = this.getOptionIndex(optionName);
          if (index < 0 || !variants.length) return true;

          // Color/scent remains selectable if it exists in any available variant.
          // Choosing it is allowed to move dependent options such as Length.
          if (this.isSwatchOption(optionName)) {
            return variants.some(
              (variant) =>
                variant?.available &&
                String(variant[`option${index + 1}`]) === String(optionValue)
            );
          }

          const selections = this.getCurrentSelections();
          return variants.some((variant) => {
            if (!variant?.available) return false;
            if (String(variant[`option${index + 1}`]) !== String(optionValue)) return false;

            return options.every((otherName, otherIndex) => {
              if (otherIndex === index) return true;
              const selectedValue = selections[otherIndex];
              if (!selectedValue) return true;
              return String(variant[`option${otherIndex + 1}`]) === String(selectedValue);
            });
          });
        },

        /* ----------------------------------------
           State
           ---------------------------------------- */
        product: normalizeProduct(pProduct),
        filter: {},

        // existing maps (used by your UI)
        firstOptionHasAvailableChildren: {},
        secondOptionHasAvailableChildren: {},

        // NEW: option1 -> option2 availability matrix + prebuilt labels
        // (Example: "613 | Platinum Blonde" -> ["16\"", "18\"", "20\""])
        firstOptionSecondAvailability: {}, // { [opt1Value]: { [opt2Value]: true/false } }
        firstOptionSecondAvailableList: {}, // { [opt1Value]: [opt2Value, ...] }
        firstOptionSecondLabel: {}, // { [opt1Value]: "16\", 18\", 20\"" }

        selectorType: 'radio',
        isProductPage: (function () {
          try {
            return window.location.pathname.includes('/products/');
          } catch (_) {
            return false;
          }
        })(),
        products: [],
        bundle: {},

        /* ----------------------------------------
           Convenience getters
           ---------------------------------------- */
        get options_by_name() {
          return this.product?.options_by_name || {};
        },
        get options() {
          return this.product?.options || [];
        },
        get variants() {
          return this.product?.variants || [];
        },
        get initial_selected_or_first_available_variant() {
          return this.product?.initial_selected_or_first_available_variant || null;
        },
        get selected_or_first_available_variant() {
          return this.product?.selected_or_first_available_variant || null;
        },
        get selected_product_id() {
          return this.product?.selected_or_first_available_variant?.id || null;
        },
        get quantity() {
          return this.product?.selected_or_first_available_variant?.quantity || 1;
        },
        set quantity(q) {
          if (this.product?.selected_or_first_available_variant) {
            this.product.selected_or_first_available_variant.quantity = Number(q) || 1;
          }
        },
        get addToCartButton() {
          return !!this.product?.selected_or_first_available_variant?.addToCartButton;
        },

        /* ----------------------------------------
           NEW: public helpers you can call from Liquid/Alpine markup
           ---------------------------------------- */

        // Returns array of available option2 values for a given option1 value (based on available variants)
        getAvailableSecondValuesForFirst(firstVal) {
          const k = String(firstVal ?? '');
          return this.firstOptionSecondAvailableList?.[k] || [];
        },

        // Returns a short, ready-to-render label (e.g. `16", 18", 20"`)
        // Use this to make it obvious that some colors have fewer sizes.
        getSecondValuesLabelForFirst(firstVal, joiner = ', ') {
          const k = String(firstVal ?? '');
          const label = this.firstOptionSecondLabel?.[k];
          if (label) return label;

          const arr = this.getAvailableSecondValuesForFirst(k);
          return arr.length ? arr.join(joiner) : '';
        },

        // For option2 dropdown rendering: is (current option1 selection + this option2 value) available?
        isSecondValueAvailable(secondVal) {
          const opts = this.product?.options || [];
          if (!opts || opts.length < 2) return true;

          const selectedFirst = this.filter[opts[0]] || this.selected_or_first_available_variant?.option1;
          const k1 = String(selectedFirst ?? '');
          const k2 = String(secondVal ?? '');

          const map = this.firstOptionSecondAvailability?.[k1];
          if (!map) return true; // fallback: do not over-disable
          return !!map[k2];
        },

        /* ----------------------------------------
           Init
           ---------------------------------------- */
        init() {
          this.bootstrapFilterFromSelected();
          this.recomputeAvailabilityMaps();

          try {
            this.activateVariants();
          } catch (_) {}
        },

        bootstrapFilterFromSelected() {
          const v = this.selected_or_first_available_variant;
          const opts = this.product?.options || [];
          const vOpts = Array.isArray(v?.options) ? v.options : [];

          opts.forEach((optName, idx) => {
            const val =
              vOpts[idx] ||
              (idx === 0 ? v?.option1 : null) ||
              (idx === 1 ? v?.option2 : null) ||
              (idx === 2 ? v?.option3 : null);

            if (val) this.filter[optName] = val;
          });
        },

        /* ----------------------------------------
           Selecting options (cards + modal)
           ---------------------------------------- */
        selectOption(optionName, optionValue) {
          if (!optionName) return;
          this.filter[optionName] = optionValue;
          this._lastChangedOptionName = optionName;
          this.setVariant(false);
        },

        // Keep your working swatch click path
        handleSwatchClick(optionName, optionValue, optIndex) {
          const hasFirstMap =
            this.firstOptionHasAvailableChildren && Object.keys(this.firstOptionHasAvailableChildren).length;
          const hasSecondMap =
            this.secondOptionHasAvailableChildren && Object.keys(this.secondOptionHasAvailableChildren).length;

          let ok = true;
          if (Number(optIndex) === 0) {
            ok = hasFirstMap ? !!this.firstOptionHasAvailableChildren?.[optionValue] : true;
          } else {
            ok = hasSecondMap ? !!this.secondOptionHasAvailableChildren?.[optionValue] : true;
          }

          if (!ok) return;
          this.selectOption(optionName, optionValue);
        },

        /* ----------------------------------------
           Variant resolution
           ---------------------------------------- */
        getSelections() {
          const opts = this.product?.options || [];
          const v = this.selected_or_first_available_variant;

          return opts
            .map((optName, idx) => {
              if (this.filter && this.filter[optName]) return this.filter[optName];
              if (Array.isArray(v?.options) && v.options[idx]) return v.options[idx];

              if (idx === 0 && v?.option1) return v.option1;
              if (idx === 1 && v?.option2) return v.option2;
              if (idx === 2 && v?.option3) return v.option3;

              return null;
            })
            .filter(Boolean);
        },

        filterVariantsBySelections(selections) {
          const vars = this.product?.variants || [];
          if (!selections.length) return vars;

          return vars.filter((v) => {
            return selections.every((val, idx) => {
              const key = `option${idx + 1}`;
              return v && v[key] === val;
            });
          });
        },

        findBestVariant(changedOptionName = null) {
          const variants = this.product?.variants || [];
          const options = this.product?.options || [];
          if (!variants.length) return null;

          const selections = this.getCurrentSelections();
          const changedIndex = changedOptionName ? this.getOptionIndex(changedOptionName) : -1;

          const matchesAll = (variant) =>
            options.every((_, index) => {
              const selectedValue = selections[index];
              return !selectedValue || String(variant[`option${index + 1}`]) === String(selectedValue);
            });

          // Exact requested combination first.
          let chosen = variants.find((variant) => variant?.available && matchesAll(variant));
          if (chosen) return chosen;

          // Preserve the option the shopper just changed. This is the important
          // Color + Length behavior: choosing a color can automatically move
          // Length to the closest valid value instead of reverting the color.
          if (changedIndex >= 0) {
            const changedValue = selections[changedIndex];
            const candidates = variants.filter(
              (variant) =>
                variant?.available &&
                String(variant[`option${changedIndex + 1}`]) === String(changedValue)
            );

            let bestScore = -1;
            candidates.forEach((variant) => {
              let score = 0;
              options.forEach((_, index) => {
                if (index === changedIndex || !selections[index]) return;
                if (String(variant[`option${index + 1}`]) === String(selections[index])) score += 1;
              });
              if (score > bestScore) {
                bestScore = score;
                chosen = variant;
              }
            });

            if (chosen) return chosen;

            // If every variant carrying the clicked value is sold out, keep that
            // value represented rather than silently switching to another value.
            const soldOutMatch = variants.find(
              (variant) => String(variant[`option${changedIndex + 1}`]) === String(changedValue)
            );
            if (soldOutMatch) return soldOutMatch;
          }

          return (
            variants.find((variant) => variant?.available) ||
            this.product?.initial_selected_or_first_available_variant ||
            variants[0] ||
            null
          );
        },

        /* ----------------------------------------
           Availability maps
           ---------------------------------------- */
        recomputeAvailabilityMaps() {
          const vars = this.product?.variants || [];
          const opts = this.product?.options || [];
          if (!vars.length || !opts.length) return;

          // First option availability (count of available variants for that option1)
          this.firstOptionHasAvailableChildren = {};
          vars.forEach((v) => {
            const key = v?.option1;
            if (!key) return;
            if (this.firstOptionHasAvailableChildren[key] === undefined) {
              this.firstOptionHasAvailableChildren[key] = 0;
            }
            this.firstOptionHasAvailableChildren[key] += v.available ? 1 : 0;
          });

          // Second option availability depends on selected first option (your existing behavior)
          this.secondOptionHasAvailableChildren = {};
          this.firstOptionSecondAvailability = {};
          this.firstOptionSecondAvailableList = {};
          this.firstOptionSecondLabel = {};

          if (opts.length > 1) {
            const firstName = opts[0];
            const secondName = opts[1];

            const secondValues =
              this.product?.options_by_name?.[secondName]?.option?.values || [];

            // Build option1 -> option2 matrix for ALL option1 values (not only selected)
            // This supports “make it more obvious” per color.
            const allFirstValues =
              this.product?.options_by_name?.[firstName]?.option?.values ||
              Array.from(new Set(vars.map((v) => v?.option1).filter(Boolean)));

            allFirstValues.forEach((firstVal) => {
              const k1 = String(firstVal);
              const map = {};
              secondValues.forEach((sv) => {
                const k2 = String(sv);
                map[k2] = vars.some((v) => v && v.available && v.option1 === k1 && v.option2 === k2);
              });
              this.firstOptionSecondAvailability[k1] = map;

              const availableList = secondValues.filter((sv) => map[String(sv)]);
              this.firstOptionSecondAvailableList[k1] = availableList;
              this.firstOptionSecondLabel[k1] = availableList.length ? availableList.join(', ') : '';
            });

            // Keep your selected-first derived map for disabling option2 choices in the UI
            const selectedFirst = this.filter[firstName] || this.selected_or_first_available_variant?.option1;
            const selectedKey = String(selectedFirst ?? '');

            secondValues.forEach((val) => {
              const k2 = String(val);
              this.secondOptionHasAvailableChildren[val] = vars.some(
                (v) => v && v.available && String(v.option1) === selectedKey && String(v.option2) === k2
              );
            });
          }
        },

        /* ----------------------------------------
           setVariant (event or boolean)
           ---------------------------------------- */
        setVariant(arg = true) {
          let pushState = true;
          let changedOptionName = this._lastChangedOptionName || null;
          this._lastChangedOptionName = null;

          if (typeof arg === 'object' && arg !== null && arg.target) {
            pushState = true;
            const name = arg.target.name;
            const val = arg.target.value;
            changedOptionName = name || changedOptionName;
            if (name && val != null) this.filter[name] = val;
          } else {
            pushState = arg;
          }

          // Single-variant shortcut
          if ((this.product?.variants || []).length === 1) {
            const sole = this.product.variants[0];
            if (!sole) return;

            this.product.selected_or_first_available_variant = normalizeVariant(sole);
            this.bootstrapFilterFromSelected();
            this.recomputeAvailabilityMaps();

            if (pushState && this.isProductPage) this.setUrlParamSafe();
            try {
              window.dispatchEvent(
                new CustomEvent('variant:changed', {
                  detail: { id: sole.id, variant: this.product.selected_or_first_available_variant },
                })
              );
            } catch (_) {}
            return;
          }

          const next = this.findBestVariant(changedOptionName);
          if (!next) return;

          this.product.selected_or_first_available_variant = normalizeVariant(next);
          this.bootstrapFilterFromSelected();
          this.recomputeAvailabilityMaps();

          if (pushState && this.isProductPage) {
            this.setUrlParamSafe();

            // Keep your optional section swap (guarded)
            const variantId = this.product.selected_or_first_available_variant?.id;
            if (variantId) {
              fetch(`${window.location.pathname}?section_id=included-products-only&variant=${variantId}`)
                .then((r) => r.text())
                .then((html) => {
                  const doc = new DOMParser().parseFromString(html, 'text/html');
                  const newSection = doc.querySelector('[data-included-products-only]');
                  const oldSection = document.querySelector('[data-included-products-only]');
                  if (!newSection || !oldSection) return;

                  oldSection.replaceChildren();
                  Array.from(newSection.childNodes).forEach((node) => oldSection.appendChild(node));

                  try {
                    if (window.Alpine && typeof window.Alpine.initTree === 'function') {
                      window.Alpine.initTree(oldSection);
                    }
                  } catch (_) {}
                })
                .catch(() => {});
            }
          }

          try {
            window.dispatchEvent(
              new CustomEvent('variant:changed', {
                detail: {
                  id: this.product.selected_or_first_available_variant?.id,
                  variant: this.product.selected_or_first_available_variant,
                },
              })
            );
          } catch (_) {}
        },

        setUrlParamSafe() {
          const vId = this.product?.selected_or_first_available_variant?.id;
          if (!vId) return;

          try {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', String(vId));
            window.history.replaceState({}, '', url.toString());
          } catch (_) {}
        },

        /* ----------------------------------------
           Compatibility: activateVariants
           ---------------------------------------- */
        activateVariants() {
          try {
            if (
              this.$el &&
              this.$el.querySelectorAll &&
              this.$el.querySelectorAll('[li-element="product-option-select"]').length > 0
            ) {
              this.selectorType = 'select';
            }
          } catch (_) {}

          this.recomputeAvailabilityMaps();
        },

        /* ----------------------------------------
           Cart helpers
           ---------------------------------------- */
        getAddToCartBody(products) {
          return JSON.stringify({
            items: (products || []).map((p) => ({
              id: p.id,
              quantity: p.quantity ?? 1,
            })),
          });
        },

        callAddToCartApi(body) {
          const root = getShopRoot();
          return fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
          });
        },

        async addToCart(e, toggleMiniCart = true, productId = undefined, quantity = undefined) {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();

          const v = this.product?.selected_or_first_available_variant;
          if (!v || !v.available) return;

          const root = getShopRoot();

          let properties = {};
          try {
            const form = this.$el?.closest('form') ?? this.$el?.querySelector('form');
            if (form && window.LiquifyHelper && typeof window.LiquifyHelper.serializeFormToObject === 'function') {
              properties = window.LiquifyHelper.serializeFormToObject(form) || {};
            }
          } catch (_) {}

          const payload = {
            items: [
              {
                id: productId ?? v.id,
                quantity: quantity ?? v.quantity ?? 1,
                properties,
              },
            ],
          };

          try {
            const res = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(payload),
            });

            const added = await res.json();

            if (!res.ok || (added && added.status && Number(added.status) >= 400)) {
              const msg = added?.message || 'Add to cart failed';
              const desc = added?.description || '';
              try {
                this.$dispatch('showcartmessage', { status: 'error', message: msg, description: desc });
              } catch (_) {}
              return;
            }

            let cart = null;
            try {
              const cartRes = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
              cart = await cartRes.json();
            } catch (_) {}

            try {
              this.$dispatch('cartupdated', cart || added);
              if (toggleMiniCart) this.$dispatch('toggleminicart');
              this.$dispatch('showcartmessage', { status: 'success', message: 'Added to cart', description: '' });
            } catch (_) {}

            try {
              window.dispatchEvent(new CustomEvent('aw-cart-updated', { detail: { cart, added } }));
              if (toggleMiniCart) window.dispatchEvent(new CustomEvent('aw-open-cart'));
            } catch (_) {}
          } catch (err) {
            try {
              this.$dispatch('showcartmessage', {
                status: 'error',
                message: 'Add to cart failed',
                description: err?.message || '',
              });
            } catch (_) {}
            console.error('Add to cart failed:', err);
          }
        },

        async addToCartAndCheckout(e, toggleMiniCart = true, productId = undefined, quantity = undefined) {
          await this.addToCart(e, toggleMiniCart, productId, quantity);
          window.location.href = '/checkout';
        },
      };
    };

    window.handleVariant = handleVariantFactory;

    try {
      if (window.Alpine && typeof window.Alpine.data === 'function') {
        window.Alpine.data('handleVariant', handleVariantFactory);
      }
    } catch (_) {}

    /* ======================================================================
       Alpine Data: Global Bundle (unchanged)
       ====================================================================== */
    const handleGlobalBundleFactory = (useQuantity = true, shopMoneyFormat) => ({
      bundle: {
        items: [],
        title: '',
        index: 1,
        get total() {
          return this.items.reduce((acc, item) => acc + Number(item.product?.price || 0) * Number(item.quantity || 0), 0);
        },
        get totalFormatted() {
          try {
            if (window.LiquifyHelper && typeof window.LiquifyHelper.moneyFormat === 'function') {
              return window.LiquifyHelper.moneyFormat(this.total, true, shopMoneyFormat);
            }
          } catch (_) {}
          return window.formatMoney(this.total);
        },
      },

      init() {
        this.loadGlobalBundle();
        this.loadBundleIndex();
      },

      handleAdd(event) {
        this.addToGlobalBundle(event.detail.variant, event.detail.product);
      },

      getGlobalBundleCartBody() {
        const globalBundle = this.getGlobalBundleFromLocalStorage();
        return JSON.stringify({
          items: globalBundle.map((item) => ({
            id: item.variant.id,
            quantity: item.quantity,
            properties: { bundle: `${this.bundle.title} ${this.bundle.index}` },
          })),
        });
      },

      callAddToCartApi(body) {
        const root = getShopRoot();
        return fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        });
      },

      async addGlobalBundleToCart() {
        const body = this.getGlobalBundleCartBody();
        try {
          const response = await this.callAddToCartApi(body);
          const data = await response.json();

          this.updateBundleIndex();
          this.clearGlobalBundle();

          try {
            this.$dispatch('cartupdated');
          } catch (_) {}
          try {
            this.$dispatch('toggleminicart');
          } catch (_) {}
          try {
            this.$dispatch('showcartmessage', {
              status: data.status,
              message: data.message,
              description: data.description,
            });
          } catch (_) {}
        } catch (error) {
          try {
            this.$dispatch('showcartmessage', {
              status: 'error',
              message: 'Error adding bundle to cart',
              description: error?.message || '',
            });
          } catch (_) {}
        }
      },

      clearGlobalBundle() {
        this.updateBundleObject([]);
      },

      addToGlobalBundle(variant, product) {
        if (useQuantity) {
          this.addOrIncreaseGlobalBundleItem(this.getNewGlobalBundleItem(variant, product, variant.quantity));
          return;
        }
        this.addNewGlobalBundleItem(this.getNewGlobalBundleItem(variant, product, variant.quantity));
      },

      getNewGlobalBundleItem(variant, product, quantity = 1) {
        variant.quantity = quantity;
        return {
          id: this.uniqueId(),
          variant,
          quantity: variant.quantity,
          product,
        };
      },

      addOrIncreaseGlobalBundleItem(item) {
        const globalBundle = this.getGlobalBundleFromLocalStorage();
        const existingItem = globalBundle.find((b) => b.variant.id === item.variant.id);

        if (existingItem) {
          let q = parseInt(existingItem.variant.quantity, 10) || 0;
          q += item.variant.quantity;
          existingItem.quantity = q;
          existingItem.variant.quantity = q;
        } else {
          globalBundle.push(item);
        }

        this.updateBundleObject(globalBundle);
      },

      addNewGlobalBundleItem(item) {
        const globalBundle = this.getGlobalBundleFromLocalStorage();
        globalBundle.push(item);
        this.updateBundleObject(globalBundle);
      },

      updateBundleObject(globalBundle) {
        this.bundle.items = globalBundle;
        localStorage.setItem('globalBundle', JSON.stringify(globalBundle));
      },

      updateBundleItem(bundleItemId, quantity) {
        this.loadGlobalBundle();

        if (quantity === 0) return this.removeProductFromGlobalBundle(bundleItemId);
        if (quantity === '-1') return this.decrementQuantityOfBundleItem(bundleItemId);
        if (quantity === '+1') return this.incrementQuantityOfBundleItem(bundleItemId);

        this.updateQuantityOfBundleItem(bundleItemId, quantity);
      },

      updateQuantityOfBundleItem(bundleItemId, quantity) {
        const bundle = this.getGlobalBundleFromLocalStorage();
        const item = bundle.find((b) => b.id === bundleItemId);
        if (!item) return;

        item.quantity = quantity;
        item.product.quantity = quantity;
        this.updateBundleObject(bundle);
      },

      decrementQuantityOfBundleItem(bundleItemId) {
        const bundle = this.getGlobalBundleFromLocalStorage();
        const item = bundle.find((b) => b.id === bundleItemId);
        if (!item) return;

        if (item.quantity === 1) return this.removeProductFromGlobalBundle(bundleItemId);

        item.quantity -= 1;
        item.product.quantity -= 1;
        this.updateBundleObject(bundle);
      },

      incrementQuantityOfBundleItem(bundleItemId) {
        const bundle = this.getGlobalBundleFromLocalStorage();
        const item = bundle.find((b) => b.id === bundleItemId);
        if (!item) return;

        const q = (parseInt(item.quantity, 10) || 0) + 1;
        item.quantity = q;
        item.product.quantity = q;
        this.updateBundleObject(bundle);
      },

      removeProductFromGlobalBundle(bundleItemId) {
        let bundle = this.getGlobalBundleFromLocalStorage();
        bundle = bundle.filter((b) => b.id !== bundleItemId);
        this.updateBundleObject(bundle);
      },

      loadGlobalBundle() {
        this.getBundleName();
        this.bundle.items = this.getGlobalBundleFromLocalStorage();
      },

      loadBundleIndex() {
        const idx = localStorage.getItem('globalBundleIndex');
        if (!idx) {
          localStorage.setItem('globalBundleIndex', '1');
          this.bundle.index = 1;
          return;
        }
        this.bundle.index = parseInt(idx, 10) || 1;
      },

      getGlobalBundleFromLocalStorage() {
        try {
          return JSON.parse(localStorage.getItem('globalBundle')) ?? [];
        } catch (_) {
          return [];
        }
      },

      getBundleName() {
        const el = document.querySelector('[data-bundlename]') || document.querySelector('[data-bundlename] *');
        this.bundle.title = el ? el.textContent.trim() : 'Bundle';
      },

      updateBundleIndex() {
        let idx = parseInt(localStorage.getItem('globalBundleIndex'), 10) || 1;
        idx += 1;
        this.bundle.index = idx;
        localStorage.setItem('globalBundleIndex', String(idx));
      },

      uniqueId() {
        return Math.trunc(Math.random() * 100000000);
      },
    });

    try {
      if (window.Alpine && typeof window.Alpine.data === 'function') {
        window.Alpine.data('handleGlobalBundle', handleGlobalBundleFactory);
      }
    } catch (_) {}
  }

  document.addEventListener('alpine:init', initLiquifyCustom);
  if (window.Alpine) initLiquifyCustom();

  /* --------------------------------------------------------------------------
     Cart page: split combined title into product title + variant/bundle lines.
     Liquify re-renders li-object="item.title" reactively, so we use a
     MutationObserver + requestAnimationFrame to always run AFTER Liquify's
     synchronous update batch settles.
     -------------------------------------------------------------------------- */
  (function () {
    function fixCartTitles() {
      document.querySelectorAll('.cart_info-order[data-product-title]').forEach(function (el) {
        const productTitle = el.dataset.productTitle;
        const variantTitle = el.dataset.variantTitle;
        const bundlePairs  = el.dataset.bundlePairs;
        const titleLink    = el.querySelector('[li-object="item.title"]');

        if (!titleLink || !productTitle) return;
        // If Liquid already rendered variant/pairs lines, do nothing (prevents duplicates)
        if (el.querySelector('.cart-item-variant')) return;

        // Fix title text if Liquify has set it to the full combined string
        if (titleLink.textContent.trim() !== productTitle) {
          titleLink.textContent = productTitle;
        }

        // Remove previously injected lines to avoid duplicates
        el.querySelectorAll('.cart-item-sub').forEach(function (s) { s.remove(); });

        // Variant line
        if (variantTitle && variantTitle !== 'Default Title' && variantTitle !== '') {
          var sub = document.createElement('div');
          sub.className = 'cart-item-sub text-size-tiny';
          sub.style.marginTop = '0.2rem';
          sub.textContent = variantTitle;
          titleLink.insertAdjacentElement('afterend', sub);
        }

        // Bundle pairs line
        if (bundlePairs) {
          var pairs = bundlePairs.split(' / ').map(function (s) { return s.trim(); }).filter(Boolean).join(' / ');
          if (pairs) {
            var sub2 = document.createElement('div');
            sub2.className = 'cart-item-sub text-size-tiny';
            sub2.style.marginTop = '0.2rem';
            sub2.textContent = pairs;
            titleLink.insertAdjacentElement('afterend', sub2);
          }
        }
      });
    }

    var _rafPending = false;
    var _cartObserver = new MutationObserver(function () {
      // Debounce via rAF — runs after Liquify's full synchronous update batch
      if (_rafPending) return;
      _rafPending = true;
      requestAnimationFrame(function () {
        _rafPending = false;
        fixCartTitles();
      });
    });

    function startCartObserver() {
      if (document.querySelector('.cart_info-order[data-product-title]')) {
        fixCartTitles();
        _cartObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
      }
    }

    document.addEventListener('DOMContentLoaded', startCartObserver);
    window.addEventListener('load', function () {
      startCartObserver();
      setTimeout(fixCartTitles, 500);
    });
  })();
})();